'use client';

import { type KeyboardEvent, useEffect, useRef, useState } from 'react';
import { Pause, Play, Quote } from 'lucide-react';

import { dureeDeLecture, TEMOIGNAGES } from '@/features/marketing/temoignages';

/** Ce que le serveur transmet : du texte seulement — une icône est une fonction, elle ne voyage pas. */
export type TemoignageAffiche = {
  id: string;
  fonctionnalite: string;
  fonction: string;
  citation: string;
  nom: string;
  entreprise: string;
};

const ICONES = Object.fromEntries(TEMOIGNAGES.map(t => [t.id, t.icon]));

const PAS: Record<string, number | undefined> = {
  ArrowRight: 1,
  ArrowDown: 1,
  ArrowLeft: -1,
  ArrowUp: -1,
};

/** « Mélanie S. » → « MS ». */
const initiales = (nom: string) =>
  nom
    .split(/\s+/)
    .map(m => m.charAt(0))
    .join('')
    .toUpperCase()
    .slice(0, 2);

/**
 * **Un témoignage à la fois, à un rythme** (#2527).
 *
 * La grille de cinq cartes se survolait : cinq pavés au même poids, rien de mis en avant. Ici la
 * citation occupe la scène, et les fonctionnalités deviennent la navigation — on choisit celle qui
 * intéresse, ou on laisse défiler.
 *
 * ⚠️ **Le défilement s'arrête dès qu'on s'y intéresse** : au survol, au focus clavier, et par le
 * bouton de pause (WCAG 2.2.2 — un contenu qui bouge seul plus de cinq secondes se met en pause).
 * Avec `prefers-reduced-motion`, il ne démarre pas : c'est la fin de l'animation de la barre qui
 * fait avancer, et la barre n'est pas animée.
 *
 * ⚠️ **Toutes les citations sont rendues, empilées dans la même case.** La case prend la hauteur de
 * la plus longue : la page ne saute pas à chaque changement, et le bouton ne se dérobe pas sous le
 * pointeur.
 */
export function CarrouselTemoignages({
  temoignages,
  libelles,
}: {
  temoignages: TemoignageAffiche[];
  libelles: { pause: string; reprendre: string; choisir: string };
}) {
  const [actif, setActif] = useState(0);
  const [enPause, setEnPause] = useState(false);
  const [survol, setSurvol] = useState(false);
  const [focus, setFocus] = useState(false);
  const departDoigt = useRef<number | null>(null);
  const liste = useRef<HTMLDivElement>(null);
  const onglets = useRef<(HTMLButtonElement | null)[]>([]);

  const n = temoignages.length;
  const plusieurs = n > 1;
  const suspendu = enPause || survol || focus;
  const aller = (i: number) => setActif(((i % n) + n) % n);

  /*
    Sur mobile, les fonctionnalités défilent en ligne : l'onglet actif doit rester visible. On fait
    défiler la liste elle-même — `scrollIntoView` ferait aussi défiler la page vers le carrousel,
    alors que le visiteur lit peut-être autre chose plus bas.
  */
  useEffect(() => {
    const l = liste.current;
    const o = onglets.current[actif];
    if (l && o && l.scrollWidth > l.clientWidth) {
      l.scrollTo({ left: o.offsetLeft - 20, behavior: 'smooth' });
    }
  }, [actif]);

  const clavier = (e: KeyboardEvent<HTMLDivElement>) => {
    const pas = PAS[e.key];
    if (!pas) return;
    e.preventDefault();
    const suivant = (((actif + pas) % n) + n) % n;
    setActif(suivant);
    onglets.current[suivant]?.focus();
  };

  return (
    <div
      className="mt-14 grid gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]"
      onMouseEnter={() => setSurvol(true)}
      onMouseLeave={() => setSurvol(false)}
      onFocus={() => setFocus(true)}
      onBlur={e => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setFocus(false);
      }}
    >
      <div
        id="temoignage-actif"
        role={plusieurs ? 'tabpanel' : undefined}
        aria-labelledby={plusieurs ? `onglet-temoignage-${temoignages[actif].id}` : undefined}
        /* Annoncer chaque changement automatique serait du bruit ; un choix, oui. */
        aria-live={suspendu ? 'polite' : 'off'}
        onTouchStart={e => {
          departDoigt.current = e.touches[0]?.clientX ?? null;
        }}
        onTouchEnd={e => {
          const depart = departDoigt.current;
          const arrivee = e.changedTouches[0]?.clientX;
          departDoigt.current = null;
          if (depart == null || arrivee == null || Math.abs(arrivee - depart) < 48) return;
          aller(actif + (arrivee < depart ? 1 : -1));
        }}
        className="relative grid overflow-hidden rounded-3xl border border-slate-200/70 bg-white p-8 shadow-xl shadow-slate-200/50 [grid-template-areas:'pile'] sm:p-10 lg:p-12"
      >
        <Quote
          aria-hidden
          strokeWidth={1.25}
          className="text-primary-50 pointer-events-none absolute -top-3 right-6 size-36 sm:size-44"
        />
        {temoignages.map((t, i) => {
          const Icone = ICONES[t.id];
          const visible = i === actif;
          return (
            <figure
              key={t.id}
              aria-hidden={!visible}
              className={`relative flex flex-col transition-all duration-500 [grid-area:pile] motion-reduce:transition-none ${
                visible ? 'visible translate-y-0 opacity-100' : 'invisible translate-y-2 opacity-0'
              }`}
            >
              <p className="border-primary-100 bg-primary-50 text-primary-700 inline-flex w-fit items-center gap-2 rounded-full border px-3 py-1.5 text-[12.5px] font-bold">
                {Icone && <Icone className="size-4" />}
                {t.fonctionnalite}
              </p>
              <blockquote className="mt-7 flex-1">
                <p className="text-lg leading-relaxed font-medium text-slate-800 sm:text-xl sm:leading-relaxed">
                  {t.citation}
                </p>
              </blockquote>
              <figcaption className="mt-8 flex items-center gap-4">
                <span
                  aria-hidden
                  className="from-primary-400 to-primary-600 shadow-primary-500/30 flex size-12 shrink-0 items-center justify-center rounded-full bg-gradient-to-br text-[15px] font-bold text-white shadow-lg"
                >
                  {initiales(t.nom)}
                </span>
                <span>
                  <span className="block text-[15px] font-bold text-slate-900">{t.nom}</span>
                  <span className="block text-[13.5px] text-slate-500">
                    {t.fonction} · {t.entreprise}
                  </span>
                </span>
              </figcaption>
            </figure>
          );
        })}
      </div>

      {plusieurs && (
        <div className="flex min-w-0 flex-col gap-3">
          <div
            ref={liste}
            role="tablist"
            aria-label={libelles.choisir}
            onKeyDown={clavier}
            className="relative -mx-5 flex snap-x gap-2 overflow-x-auto px-5 pb-1 lg:mx-0 lg:flex-col lg:overflow-visible lg:px-0 lg:pb-0"
          >
            {temoignages.map((t, i) => {
              const Icone = ICONES[t.id];
              const choisi = i === actif;
              return (
                <button
                  key={t.id}
                  ref={el => {
                    onglets.current[i] = el;
                  }}
                  type="button"
                  role="tab"
                  id={`onglet-temoignage-${t.id}`}
                  aria-selected={choisi}
                  aria-controls="temoignage-actif"
                  tabIndex={choisi ? 0 : -1}
                  onClick={() => aller(i)}
                  className={`focus-visible:ring-primary-500/40 relative flex shrink-0 snap-start items-center gap-3 overflow-hidden rounded-2xl border px-4 py-3 text-left transition-all focus-visible:ring-3 focus-visible:outline-none lg:py-3.5 ${
                    choisi
                      ? 'border-primary-200 bg-white shadow-lg shadow-slate-200/60'
                      : 'border-transparent hover:border-slate-200 hover:bg-white/70'
                  }`}
                >
                  <span
                    className={`flex size-9 shrink-0 items-center justify-center rounded-xl border ${
                      choisi
                        ? 'border-primary-100 bg-primary-50 text-primary-600'
                        : 'border-slate-200 bg-white text-slate-400'
                    }`}
                  >
                    {Icone && <Icone className="size-4" />}
                  </span>
                  <span className="min-w-0">
                    <span
                      className={`block text-[13.5px] font-bold whitespace-nowrap lg:whitespace-normal ${
                        choisi ? 'text-slate-900' : 'text-slate-600'
                      }`}
                    >
                      {t.fonctionnalite}
                    </span>
                    <span className="hidden text-[12.5px] text-slate-500 lg:block">{t.nom}</span>
                  </span>
                  {choisi && (
                    <span aria-hidden className="bg-primary-100 absolute inset-x-0 bottom-0 h-0.5">
                      {/* La fin de cette animation fait avancer : c'est l'unique minuterie. */}
                      <span
                        key={actif}
                        className="animate-temoignage-progression bg-primary-500 block h-full origin-left"
                        style={{
                          animationDuration: `${dureeDeLecture(t.citation)}s`,
                          animationPlayState: suspendu ? 'paused' : 'running',
                        }}
                        onAnimationEnd={() => aller(actif + 1)}
                      />
                    </span>
                  )}
                </button>
              );
            })}
          </div>
          {/* Sans mouvement, rien ne défile : le bouton n'aurait rien à arrêter. */}
          <button
            type="button"
            onClick={() => setEnPause(p => !p)}
            className="hover:text-primary-700 focus-visible:ring-primary-500/40 inline-flex w-fit items-center gap-2 self-end rounded-full px-3 py-1.5 text-[12.5px] font-semibold text-slate-500 transition-colors focus-visible:ring-3 focus-visible:outline-none motion-reduce:hidden"
          >
            {enPause ? <Play className="size-3.5" /> : <Pause className="size-3.5" />}
            {enPause ? libelles.reprendre : libelles.pause}
          </button>
        </div>
      )}
    </div>
  );
}

export default CarrouselTemoignages;
